import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowUpRight, ArrowLeft, ArrowRight } from "lucide-react";
import { SERVICES, COMPANY } from "../data/content";
import { Reveal, MaskLine, EASE } from "../lib/motion";
import ServiceIcon from "../components/ServiceIcon";

export default function ServiceDetail() {
  const { id } = useParams();
  const idx = SERVICES.findIndex((s) => s.id === id);
  if (idx === -1) return <Navigate to="/services" replace />;

  const s = SERVICES[idx];
  const next = SERVICES[(idx + 1) % SERVICES.length];

  return (
    <div data-testid="service-detail-page">
      {/* Hero */}
      <section className="relative overflow-hidden pt-36 pb-16 md:pt-44 md:pb-24">
        <div className="absolute inset-0 z-0">
          <img src={s.image} alt={s.title} className="h-full w-full object-cover opacity-30 grayscale" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/70 via-[#050505]/85 to-[#050505]" />
        </div>
        <div className="relative z-10 mx-auto max-w-[1400px] px-5 md:px-10">
          <Link to="/services" data-testid="back-to-services" className="inline-flex items-center gap-2 text-sm text-neutral-400 hover:text-accent transition-colors">
            <ArrowLeft size={16} /> All services
          </Link>
          <div className="mt-8 flex items-center gap-4">
            <span className="flex h-14 w-14 items-center justify-center rounded-full border border-white/15 text-accent">
              <ServiceIcon name={s.icon} size={26} />
            </span>
            <span className="font-display text-2xl font-black text-white/30">{s.index}</span>
          </div>
          <h1 className="font-display mt-6 text-5xl font-black leading-[0.95] tracking-tighter md:text-7xl lg:text-8xl">
            <MaskLine delay={0.2}>{s.title}</MaskLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.7, ease: EASE }}
            className="mt-6 text-sm uppercase tracking-[0.2em] font-bold text-accent"
          >
            {s.tagline}
          </motion.p>
        </div>
      </section>

      {/* Body */}
      <section className="mx-auto max-w-[1400px] px-5 pb-24 md:px-10 md:pb-32">
        <div className="grid gap-14 lg:grid-cols-[1.4fr_1fr]">
          <Reveal>
            <p className="text-xl md:text-2xl leading-relaxed text-neutral-200 font-medium">{s.overview}</p>
            <div className="mt-8 space-y-6">
              {s.paragraphs.map((p, i) => (
                <p key={i} className="text-base md:text-lg leading-relaxed text-neutral-400">{p}</p>
              ))}
            </div>
            <Link
              to="/contact"
              data-testid="service-quote-btn"
              className="mt-10 inline-flex items-center gap-2 rounded-full bg-accent px-6 py-3.5 text-sm font-bold text-black transition-transform duration-200 hover:scale-[0.97]"
            >
              Request a Quote for {s.title.split(" ")[0]} <ArrowUpRight size={16} />
            </Link>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-8">
              <h3 className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-bold">What&apos;s included</h3>
              <ul className="mt-6 space-y-4">
                {s.features.map((f, i) => (
                  <li key={i} className="flex gap-3 text-neutral-200">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent/15 text-accent">
                      <Check size={13} />
                    </span>
                    <span className="text-sm md:text-base leading-relaxed">{f}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 border-t border-white/10 pt-6 text-sm text-neutral-400">
                Available across {COMPANY.cities.join(", ")}.
                <a href={`tel:${COMPANY.phone}`} className="mt-2 block font-semibold text-accent">{COMPANY.phoneDisplay}</a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Next service */}
      <section className="border-t border-white/10 bg-[#080808]">
        <Link
          to={`/services/${next.id}`}
          data-testid="next-service"
          className="group mx-auto flex max-w-[1400px] items-center justify-between px-5 py-14 md:px-10 md:py-20"
        >
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-bold">Next service</p>
            <h3 className="font-display mt-3 text-3xl md:text-5xl font-black tracking-tight transition-colors group-hover:text-accent">
              {next.title}
            </h3>
          </div>
          <ArrowRight size={40} className="text-accent transition-transform group-hover:translate-x-2" />
        </Link>
      </section>
    </div>
  );
}
